import { action, makeAutoObservable, observable } from "mobx";
import {
  CheckoutColorType,
  CheckOutPayButtonCaption,
  UpsertPurchaseActiveStep,
} from "../enums";
import CountryStore from "./CountryStore";
import {
  OptionSectionModel,
  PurchasePaymentModel,
} from "../models/PurchaseSectionModel";
import { CustomerFacingCheckOutModel } from "../models/PurchaseModel";
import {
  PurchaseCheckOutDetails,
  PurchaseCheckoutItemsDoneModel,
  PurchasePaymentIntentModel,
} from "../models/PurchaseCheckOutDetails";
import { CustomerFacingPaymentModel } from "../models/CustomerFacingPaymentModel";
import { CheckoutMainDetailPageModel } from "../models/CheckoutModel";
import { AddressModel } from "../models/AddressModel";
import {
  BasicColors,
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  CustomColors,
} from "../models/CheckoutOptionsPageModel";
import {
  CheckoutPaymentPageModel,
  CheckOutPayPageFormFields,
} from "../models/CheckoutPaymentPageModel";
import {
  IAPICheckOutPaymentModel,
  IAPICheckOutPurchaseModel,
  IAPICustomerFacingCheckOutModel,
  IAPIPurchaseCheckOutItemsModel,
} from "../interfaces/IAPIPurchaseModels";
import { IAPIPurchaselinkModel } from "../interfaces/IAPIPurchaseLinkModel";
import {
  CustomerFacingCheckOutItemModel,
  getPurchaseItemsPriceDetails,
  PurchaseCheckOutAddOnDetails,
} from "../models/PriceUtility";
import { ICheckOutCombinedColors } from "../interfaces/IAPICheckoutModel";
import { PurchaseItemsPriceModel } from "../models/PurchaseItemsPriceModel";
import { CountryWithStateModel, CustomOptionModel } from "../models";
import { ToastService } from "@mindmemobile/mm-shared-global";
import { PromotionModel } from "../models/PromotionModels";

export default class PurchaseStore {
  @observable activeStep: UpsertPurchaseActiveStep;
  @observable activeStepURL: string = "";
  @observable optionSectiondata: OptionSectionModel;
  @observable paymentSectionData: PurchasePaymentModel;
  @observable customerFacingCheckOutDetails: CustomerFacingCheckOutModel;
  @observable checkOutPurchaseDetails: PurchaseCheckOutDetails | null = null;
  @observable customerFacingPaymentData: CustomerFacingPaymentModel;
  @observable paymentIntent: PurchasePaymentIntentModel | null = null;
  @observable activeNavPill: string = "";
  @observable purchaseDone: boolean = false;
  @observable purchaseDoneResponse: PurchaseCheckoutItemsDoneModel | null =
    null;
  @observable checkoutDetailPageMain: CheckoutMainDetailPageModel;
 
  constructor(countryStore?: CountryStore) {
    this.activeStep = UpsertPurchaseActiveStep.Purchase;
    this.activeStepURL = "";
    this.optionSectiondata = new OptionSectionModel();
    this.customerFacingCheckOutDetails = new CustomerFacingCheckOutModel({});
    this.customerFacingPaymentData = new CustomerFacingPaymentModel();
    this.paymentSectionData = new PurchasePaymentModel();
    this.checkoutDetailPageMain = new CheckoutMainDetailPageModel();
    this.purchaseDoneResponse = new PurchaseCheckoutItemsDoneModel();
    makeAutoObservable(this);

    // this.countryStore.onCountryDataLoaded.subscribe((countryData) => {
    //   if (countryData) {
    //     const usCountry = countryData.find((c) => c.abbreviationName === "US");
    //     if (usCountry) {
    //       this.initPurchaseSectionData(usCountry);
    //     }
    //   }
    // });
    // const usCountry1 = this.countryStore.getUSCountry();
    // if (usCountry1) {
    //   this.initPurchaseSectionData(usCountry1);
    // }
  }

  initPurchaseSectionData(usCountry: CountryWithStateModel) {
    if (!usCountry) return;
    this.paymentSectionData = new PurchasePaymentModel({
      ...this.paymentSectionData,
      countryDetails: usCountry,
      billingAddress: new AddressModel({ countryDetails: usCountry }),
      shippingAddress: new AddressModel({ countryDetails: usCountry }),
    });
  }

  @action
  toJSON() {
    return {
      customerFacingCheckOutDetails: new CustomerFacingCheckOutModel({
        ...this.customerFacingCheckOutDetails,
        options: new CheckoutOptionsPageModel({
          ...this.customerFacingCheckOutDetails.options,
          brandingData: new CheckoutBrandingModel({
            ...this.customerFacingCheckOutDetails.options.brandingData,
            customColors: new CustomColors({
              ...this.customerFacingCheckOutDetails.options.brandingData
                .customColors,
            }),
            basicColors: new BasicColors({
              ...this.customerFacingCheckOutDetails.options.brandingData
                .basicColors,
            }),
          }),
        }),
      }),
    };
  }

  @action
  updateDetailPageMain(data: CheckoutMainDetailPageModel) {
    this.checkoutDetailPageMain = new CheckoutMainDetailPageModel(data);
  }

  @action
  initializeFormData(
    paymentSectionData: PurchasePaymentModel,
    payDetails: CheckoutPaymentPageModel
  ) {
    const { formData } = paymentSectionData;
    const { formFields } = payDetails.formData;
    const initializedFormData: { [key: string]: string } = {};

    formFields.forEach((field: CheckOutPayPageFormFields) => {
      if (field.isRequired) {
        if (field.field === "FirstLastName") {
          initializedFormData["firstName"] = formData["firstName"] || "";
          initializedFormData["lastName"] = formData["lastName"] || "";
        } else {
          initializedFormData[field.field] = formData[field.field] || "";
        }
      }
    });

    this.paymentSectionData = new PurchasePaymentModel({
      ...paymentSectionData,
      formData: { ...formData, ...initializedFormData },
    });
  }

  @action
  togglePurchaseDone() {
    this.purchaseDone = !this.purchaseDone;
  }

  @action
  setPurchaseDoneResponse(
    data: IAPIPurchaseCheckOutItemsModel,
    isDonation: boolean,
    donationFrequency: string
  ) {
    this.purchaseDoneResponse =
      PurchaseCheckoutItemsDoneModel.deserializeAPIModel(
        data,
        isDonation,
        donationFrequency
      );
  }

  updatePaymentIntent(paymentIntent: PurchasePaymentIntentModel) {
    this.paymentIntent = paymentIntent;
  }

  updateActiveNavPill(activeNavPill: string) {
    this.activeNavPill = activeNavPill;
  }

  updateOptionsPageData(data: OptionSectionModel) {
    this.optionSectiondata = new OptionSectionModel(data);
  }

  updatePaymentSectionData(data: PurchasePaymentModel) {
    this.paymentSectionData = new PurchasePaymentModel(data);
  }

  reset(countryStore: CountryStore) {
    this.activeStep = UpsertPurchaseActiveStep.Purchase;
    this.activeStepURL = "";
    this.optionSectiondata = new OptionSectionModel();
    this.customerFacingCheckOutDetails = new CustomerFacingCheckOutModel({});
    this.checkOutPurchaseDetails = null;
    this.activeNavPill = "";
    this.paymentIntent = null;
    this.customerFacingPaymentData = new CustomerFacingPaymentModel();
    this.paymentSectionData = new PurchasePaymentModel({
      countryDetails: countryStore.getUSCountry(),
    });
    this.purchaseDone = false; 
  }

  showCheckOutAddOns() {
    if (!this.checkOutPurchaseDetails?.item) {
      return false;
    }

    if (
      !this.checkOutPurchaseDetails.item.requiredItems &&
      !this.checkOutPurchaseDetails.item.recommendedItems
    ) {
      return false;
    }

    const hasRelatedItems =
      (this.checkOutPurchaseDetails.item.requiredItems &&
        this.checkOutPurchaseDetails.item.requiredItems?.length > 0) ||
      (this.checkOutPurchaseDetails.item.recommendedItems &&
        this.checkOutPurchaseDetails.item.recommendedItems?.length > 0);
    return hasRelatedItems;
  }

  updateCheckOutPurchaseDetails(
    checkOutPurchaseDetails: PurchaseCheckOutDetails
  ) {
    this.checkOutPurchaseDetails = new PurchaseCheckOutDetails(
      checkOutPurchaseDetails
    );
    //this.checkOutPurchaseDetails.item.validate();
    // this.checkOutPurchaseDetails = new PurchaseCheckOutDetails(
    //   checkOutPurchaseDetails
    // );
  }

  deserializeCustomerFacingCheckOutModel(
    data: IAPICustomerFacingCheckOutModel
  ) {
    this.customerFacingCheckOutDetails =
      CustomerFacingCheckOutModel.deserializeAPIModel(data);
    const groupedItems =
      CustomerFacingCheckOutItemModel.groupByBillingFrequency(
        this.customerFacingCheckOutDetails.items,
        data.customOption &&
        CustomOptionModel.deserializeAPIModel(data.customOption)
      );

    // if (groupedItems.length > 0) {
    //   this.activeNavPill = groupedItems[0].name;
    // }
  }

  deserializePurchaseLinkToCustomerFacing(data: IAPIPurchaselinkModel) {
    this.customerFacingCheckOutDetails =
      CustomerFacingCheckOutModel.deserializePurchaseLinkAPIModel(data);
    const groupedItems =
      CustomerFacingCheckOutItemModel.groupByBillingFrequency(
        this.customerFacingCheckOutDetails.items
      );

    if (groupedItems.length > 0) {
      this.activeNavPill = groupedItems[0].name;
    }
  }

  updateCustomerFacingCheckOutModel(data?: CustomerFacingCheckOutModel) {
    this.customerFacingCheckOutDetails = new CustomerFacingCheckOutModel(data);
  }

  updateCustomerFacingPaymentData(
    customerFacingPaymentData: CustomerFacingPaymentModel
  ) {
    this.customerFacingPaymentData = new CustomerFacingPaymentModel(
      customerFacingPaymentData
    );
  }

  @action
  updateActiveStep(activeStep: UpsertPurchaseActiveStep) {
    this.activeStep = activeStep;
  }

  @action
  updateActiveStepURL(activeStepURL: string) {
    this.activeStepURL = activeStepURL;
  }

  @action
  validate(step?: UpsertPurchaseActiveStep): boolean {
    let isValid = true;
    const isTabChange = step ? true : false;
    const stepToValidate = isTabChange ? step : this.activeStep;
    if (!this.checkOutPurchaseDetails) return false;
    switch (stepToValidate) {
      case UpsertPurchaseActiveStep.Purchase:
        isValid = this.checkOutPurchaseDetails ? true : false;
        break;
      case UpsertPurchaseActiveStep.AddOn: {
        const allowedRequiredItems =
          this.checkOutPurchaseDetails?.item?.getAllowedRequiredItems() || [];
        let requiredItems =
          this.checkOutPurchaseDetails?.item.relatedItems?.requiredItems
            .totalRequired || 0;
        const requiredAddOnsCount =
          this.checkOutPurchaseDetails?.addOns?.filter((addOn) =>
            allowedRequiredItems.find((a) => a.id === addOn.itemId)
          )?.length || 0;
        const totalRequiredItems = allowedRequiredItems.length;
        requiredItems =
          requiredItems > totalRequiredItems
            ? totalRequiredItems
            : requiredItems;

        // const totalRequiredInStock =
        //     this.checkOutPurchaseDetails?.item?.requiredItems?.filter((x) => x.isOutOfStock === false)
        //         ?.length || 0;

        const totalInStockAddOns =
          allowedRequiredItems.filter(
            (addOn) => addOn.isItemUnavailable === false
          )?.length || 0;

        const requiredAddOns =
          totalInStockAddOns < requiredItems
            ? totalInStockAddOns
            : requiredItems;

        isValid =
          requiredAddOns > 0 ? requiredAddOnsCount >= requiredAddOns : true;
        if (!isValid) {
          ToastService.showError(
            `Please select at least ${requiredItems} required items to proceed with the purchase`
          );
        }
        break;
      }
      case UpsertPurchaseActiveStep.Options:
        isValid = this.checkOutPurchaseDetails?.item.validate();
        const addOnValidation = this.checkOutPurchaseDetails?.addOns.every(
          (addOn) => addOn.validate()
        );
        isValid = isValid && addOnValidation;
    }

    this.checkOutPurchaseDetails = new PurchaseCheckOutDetails(
      this.checkOutPurchaseDetails
    );
    return isValid;
  }

  @action
  validatePreviousSteps(currentStep: UpsertPurchaseActiveStep): {
    isValid: boolean;
    errorStep?: UpsertPurchaseActiveStep;
  } {
    const steps = [
      UpsertPurchaseActiveStep.Purchase,
      UpsertPurchaseActiveStep.AddOn,
      UpsertPurchaseActiveStep.Options,
      UpsertPurchaseActiveStep.Payment,
    ];

    const currentStepIndex = steps.indexOf(currentStep);
    for (let i = 0; i < currentStepIndex; i++) {
      const step = steps[i];
      const requiredItems =
        this.checkOutPurchaseDetails?.item.relatedItems?.requiredItems
          .totalRequired || 0;
      if (requiredItems < 1 && step === UpsertPurchaseActiveStep.AddOn) {
        continue;
      }
      if (!this.validate(step)) {
        return { isValid: false, errorStep: step };
      }
    }
    return { isValid: true };
  }

  calculateTotalPrice = (
    items: CustomerFacingCheckOutItemModel[],
    addOns: PurchaseCheckOutAddOnDetails[],
    surchargePercentage?: number,
    promotionRelatedData?: PromotionModel
  ): number => {
    const purchaseDetails: PurchaseItemsPriceModel[] =
      getPurchaseItemsPriceDetails(
        items,
        addOns,
        surchargePercentage,
        promotionRelatedData
      );
    const totalPrice = purchaseDetails
      .filter((priceDetail) => !priceDetail.dueLater)
      .reduce((total, priceDetail) => {
        return total + priceDetail.totalPrice;
      }, 0);
    return totalPrice;
  };

  createCheckOutAPIPurchaseModel(
    checkOutId: string | null,
    surchargePercentage?: number,
    promotionRelatedData?: PromotionModel,
    isFromFundraiser?: boolean,
    promoterId?: string
  ): IAPICheckOutPurchaseModel {
    const amount = this.calculateTotalPrice(
      [
        this.checkOutPurchaseDetails?.item ||
        new CustomerFacingCheckOutItemModel(),
      ],
      this.checkOutPurchaseDetails?.addOns || [],
      surchargePercentage,
      promotionRelatedData ? promotionRelatedData : undefined
    );

    const amountToPay = parseFloat(amount.toFixed(2));
    return {
      id: checkOutId,
      accountId: this.customerFacingCheckOutDetails.accountId,
      purchaseItems: this.checkOutPurchaseDetails?.createItemsAPIModel() || [],
      addOns: this.checkOutPurchaseDetails?.createAddOnsAPIModel() || null,
      paymentDetails: this.paymentIntent
        ? this.paymentIntent.createAPIModel(
          this.paymentSectionData.paymentMethodId,
          amountToPay
        )
        : ({
          amount: amountToPay,
          paymentMethodId: this.paymentSectionData.paymentMethodId,
          paymentIntentId: "",
        } as IAPICheckOutPaymentModel),
      billingAddress: this.customerFacingCheckOutDetails.paymentDetails.formData
        .isBillingAddressChecked
        ? this.paymentSectionData.billingAddress.createAPIModel()
        : null,
      shippingAddress: this.customerFacingCheckOutDetails.paymentDetails
        .formData.isShippingAddressChecked
        ? this.paymentSectionData.shippingAddress.createAPIModel()
        : null,
      contactFields:
        this.paymentSectionData.createContactFieldsAPIModel() || [],
      coverProcessingCost:
        surchargePercentage && surchargePercentage > 0 ? true : false,
      promocodeId: this.paymentSectionData.promotionRelatedData?.id || "",
      isFromFundRaiser: isFromFundraiser || false,
      promoterId: promoterId || undefined
    };
  }
}
